import { HttpClient, HttpError } from './httpClient';

export type ApiResponse<T extends { [key: string]: any } = {}> = T & {
  success: true;

  /**
   * Some meaningless message just to eat some traffic
   */
  message?: string;
};

export interface ApiErrorResponse {
  success: false;

  /**
   * Error message in case when success === false
   */
  message: string;

  /**
   * Error code in case success === false
   */
  code?: string;
}

export class ApiError extends HttpError {
  code: string | null = null;

  /**
   * A message that is safe to display to user
   */
  userMessage: string | null = null;

  constructor({
    statusCode = 200,
    body,
  }: {
    statusCode?: number;
    body: ApiErrorResponse;
  }) {
    super({ statusCode, body: '' });

    if (body.code) {
      this.code = body.code;
    }

    this.message = body.message || 'Unknown error';

    if (body.message) {
      this.userMessage = body.message;
    }
  }
}

class EstClient extends HttpClient {
  private csrfToken: string | null = null;

  protected async request<T>(params: Parameters<HttpClient['request']>[0]) {
    let { body } = params;

    if (params.method !== 'GET') {
      if (!this.csrfToken) {
        throw new Error('CSRF token is required');
      }

      body = {
        ...body,
        token: this.csrfToken,
      };
    }

    try {
      const resp = await super.request<T | ApiErrorResponse>({
        ...params,
        body,
      });

      if (resp && (resp as ApiErrorResponse).success === false) {
        throw new ApiError({
          statusCode: 200,
          body: resp as ApiErrorResponse,
        });
      }

      return resp as T;
    } catch (error) {
      if (
        error instanceof HttpError &&
        error.json &&
        error.json.success === false
      ) {
        // sometimes we can get success: false with statusCode === 500
        throw new ApiError({
          statusCode: error.statusCode,
          body: error.json as ApiErrorResponse,
        });
      }

      throw error;
    }
  }

  setCsrfToken(newToken: string | null) {
    this.csrfToken = newToken;
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }
}

export const estClient = new EstClient();
